// Sentry browser SDK init with aggressive PHI scrubbing.
//
// Outside the BAA ring (Sentry V1 is on the Developer tier; BAA blocked
// by Sentry Business tier pricing). Every event therefore runs through
// beforeSend + beforeBreadcrumb to redact PHI-adjacent keys and value
// patterns (SSN, email, DOB, phone). User identity is reduced to user
// id only; email/ip stripped.
//
// Session Replay is intentionally NOT enabled. Replay captures full DOM
// + network snapshots and is incompatible with the PHI architecture
// regardless of scrubber.

import * as Sentry from '@sentry/react';
import { env } from '../env';

const PHI_KEY_PATTERNS: readonly RegExp[] = [
  /\bmedication[_-]?name\b/i,
  /\bndc\b/i,
  /\brxcui\b/i,
  /\bdob\b/i,
  /\bdate[_-]?of[_-]?birth\b/i,
  /\bcondition\b/i,
  /\ballergy\b/i,
  /\bhousehold[_-]?member\b/i,
  /\bprescriber[_-]?name\b/i,
  /\bpharmacy[_-]?name\b/i,
  /\bfamily[_-]?email\b/i,
  /\bphone\b/i,
  /\baddress\b/i,
  /\bzip\b/i,
];

const PHI_VALUE_PATTERNS: readonly RegExp[] = [
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
  /\b\d{3}-\d{2}-\d{4}\b/g,
  /\b(?:0?[1-9]|1[0-2])[/-](?:0?[1-9]|[12]\d|3[01])[/-](?:19|20)\d{2}\b/g,
  /\b\+?1?[ -]?\(?\d{3}\)?[ -]?\d{3}[ -]?\d{4}\b/g,
];

function scrubString(s: string): string {
  let out = s;
  for (const p of PHI_VALUE_PATTERNS) out = out.replace(p, '[REDACTED]');
  return out;
}

function scrubObject(obj: unknown, depth = 0): unknown {
  if (depth > 6) return '[TRUNCATED]';
  if (typeof obj === 'string') return scrubString(obj);
  if (Array.isArray(obj)) return obj.map((v) => scrubObject(v, depth + 1));
  if (obj && typeof obj === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (PHI_KEY_PATTERNS.some((p) => p.test(k))) {
        out[k] = '[REDACTED]';
      } else {
        out[k] = scrubObject(v, depth + 1);
      }
    }
    return out;
  }
  return obj;
}

export function initSentryWeb(): void {
  if (!env.sentryDsn) return;
  Sentry.init({
    dsn: env.sentryDsn,
    environment: env.appEnv,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.request) event.request = scrubObject(event.request) as typeof event.request;
      if (event.extra) event.extra = scrubObject(event.extra) as typeof event.extra;
      if (event.contexts) event.contexts = scrubObject(event.contexts) as typeof event.contexts;
      if (event.user) event.user = event.user.id ? { id: event.user.id } : undefined;
      if (event.message) event.message = scrubString(event.message);
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((b) => ({
          ...b,
          message: b.message ? scrubString(b.message) : b.message,
          data: b.data ? (scrubObject(b.data) as typeof b.data) : b.data,
        }));
      }
      return event;
    },
  });
}
